import { z } from "zod";
import { AccessError, canonicalRoles, roleIds, scopedId, type RoleId } from "./contracts";

const revision = z.number().int().safe().nonnegative();
const name = z.string().trim().min(1).max(200);
export const workspaceSiteSchema = z.object({
  id: scopedId, name, active: z.boolean(), revision,
}).strict();
export const workspaceCompanySchema = z.object({
  id: scopedId, siteId: scopedId, tenantId: scopedId, name,
  active: z.boolean(), revision,
}).strict();
export const workspaceGrantSchema = z.discriminatedUnion("scope", [
  z.object({ id: scopedId, subjectId: scopedId, scope: z.literal("site"), siteId: scopedId, roleIds: canonicalRoles, active: z.boolean(), revision }).strict(),
  z.object({ id: scopedId, subjectId: scopedId, scope: z.literal("company"), companyId: scopedId, roleIds: canonicalRoles, active: z.boolean(), revision }).strict(),
]);
export const workspacePreferenceSchema = z.object({
  subjectId: scopedId,
  siteId: scopedId.optional(),
  companies: z.array(z.object({ siteId: scopedId, companyId: scopedId }).strict()).max(1000),
}).strict();
export const workspaceDirectorySchema = z.object({
  version: z.literal(1), revision,
  sites: z.array(workspaceSiteSchema).max(1000),
  companies: z.array(workspaceCompanySchema).max(10000),
  grants: z.array(workspaceGrantSchema).max(100000),
  preferences: z.array(workspacePreferenceSchema).max(100000),
}).strict().superRefine((state, context) => {
  const unique = (values: string[]) => new Set(values).size === values.length;
  const bad = () => context.addIssue({ code: "custom", message: "Invalid workspace directory relationships" });
  if (!unique(state.sites.map(s => s.id)) || !unique(state.companies.map(c => c.id)) ||
      !unique(state.companies.map(c => c.tenantId)) || !unique(state.grants.map(g => g.id)) ||
      !unique(state.preferences.map(p => p.subjectId)) ||
      !unique(state.grants.map(g => JSON.stringify([g.subjectId, g.scope, g.scope === "site" ? g.siteId : g.companyId])))) bad();
  if (state.companies.some(c => !state.sites.some(s => s.id === c.siteId)) ||
      state.grants.some(g => g.scope === "site" ? !state.sites.some(s => s.id === g.siteId) : !state.companies.some(c => c.id === g.companyId)) ||
      [...state.sites, ...state.companies, ...state.grants].some(row => row.revision > state.revision) ||
      state.preferences.some(p => !unique(p.companies.map(c => c.siteId)))) bad();
});
export type WorkspaceDirectory = z.infer<typeof workspaceDirectorySchema>;
export type WorkspaceGrant = z.infer<typeof workspaceGrantSchema>;
export interface WorkspaceDirectoryStore {
  read(): Promise<WorkspaceDirectory>;
  compareAndSwap(next: WorkspaceDirectory, expectedRevision: number): Promise<void>;
}
export class MemoryWorkspaceDirectoryStore implements WorkspaceDirectoryStore {
  protected state: WorkspaceDirectory;
  private writes: Promise<unknown> = Promise.resolve();
  constructor(initial: unknown = { version: 1, revision: 0, sites: [], companies: [], grants: [], preferences: [] }) {
    this.state = workspaceDirectorySchema.parse(initial);
  }
  async read() { await this.writes; return structuredClone(this.state); }
  protected async persist(_next: WorkspaceDirectory) {}
  async compareAndSwap(raw: WorkspaceDirectory, expectedRevision: number) {
    const next = workspaceDirectorySchema.parse(raw);
    const write = this.writes.then(async () => {
      if (this.state.revision !== expectedRevision || next.revision !== expectedRevision + 1)
        throw new AccessError("WORKSPACE_DIRECTORY_STALE", 409);
      // A tenant is a permanent data boundary; updates cannot move companies or reuse its identity.
      for (const previous of this.state.companies) {
        const current = next.companies.find(c => c.id === previous.id);
        if (!current || current.siteId !== previous.siteId || current.tenantId !== previous.tenantId)
          throw new AccessError("WORKSPACE_COMPANY_IDENTITY_IMMUTABLE", 409);
      }
      for (const previous of this.state.sites)
        if (!next.sites.some(s => s.id === previous.id)) throw new AccessError("WORKSPACE_SITE_IDENTITY_IMMUTABLE", 409);
      for (const previous of this.state.grants) {
        const current = next.grants.find(g => g.id === previous.id);
        if (!current || grantIdentity(current) !== grantIdentity(previous))
          throw new AccessError("WORKSPACE_GRANT_IDENTITY_IMMUTABLE", 409);
      }
      await this.persist(structuredClone(next));
      this.state = next;
    });
    this.writes = write.catch(() => {});
    await write;
  }
}
const grantIdentity = (g: WorkspaceGrant) => JSON.stringify([g.id, g.subjectId, g.scope, g.scope === "site" ? g.siteId : g.companyId]);
export const workspaceSelectionSchema = z.object({ siteId: scopedId, companyId: scopedId }).strict();
export type WorkspaceSelection = z.infer<typeof workspaceSelectionSchema>;
export interface WorkspaceActor {
  /** Trusted authenticated account identity, never request-body claims. */
  subjectId: string;
  /** Set only by a trusted platform administrator adapter. */
  root?: boolean;
}
const relevant = (state: WorkspaceDirectory, subject: string, company: z.infer<typeof workspaceCompanySchema>) =>
  state.grants.filter(g => g.subjectId === subject &&
    (g.scope === "site" ? g.siteId === company.siteId : g.companyId === company.id));
function decision(state: WorkspaceDirectory, subject: string, raw: WorkspaceSelection) {
  scopedId.parse(subject);
  const selection = workspaceSelectionSchema.parse(raw);
  const site = state.sites.find(s => s.id === selection.siteId && s.active);
  const company = state.companies.find(c => c.id === selection.companyId && c.siteId === site?.id && c.active);
  if (!site || !company) throw new AccessError("WORKSPACE_ACCESS_DENIED", 403);
  const grants = relevant(state, subject, company).sort((a, b) => a.id.localeCompare(b.id));
  const roles = roleIds.filter(role => grants.some(g => g.active && g.roleIds.includes(role)));
  if (!roles.length) throw new AccessError("WORKSPACE_ACCESS_DENIED", 403);
  return {
    siteId: site.id, companyId: company.id, tenantId: company.tenantId,
    subjectId: subject, roleIds: roles,
    // Include inactive relevant grants so revoke/regrant never restores an old authorization identity.
    authorizationKey: JSON.stringify([site.id, site.revision, company.id, company.revision, grants.map(g => [g.id, g.revision, g.active, g.roleIds])]),
  };
}
function available(state: WorkspaceDirectory, subject: string) {
  const sites = state.sites.filter(s => s.active).map(site => ({
    id: site.id, name: site.name,
    companies: state.companies.filter(c => c.siteId === site.id && c.active).flatMap(company => {
      try { const access = decision(state, subject, { siteId: site.id, companyId: company.id }); return [{ id: company.id, name: company.name, tenantId: company.tenantId, roleIds: access.roleIds }]; }
      catch (error) { if (error instanceof AccessError) return []; throw error; }
    }),
  })).filter(site => site.companies.length);
  const saved = state.preferences.find(p => p.subjectId === subject);
  const site = sites.find(s => s.id === saved?.siteId) || (sites.length === 1 ? sites[0] : undefined);
  const company = site?.companies.find(c => c.id === saved?.companies.find(p => p.siteId === site.id)?.companyId) || (site?.companies.length === 1 ? site.companies[0] : undefined);
  return {
    version: 1 as const, revision: state.revision, subjectId: subject, sites,
    defaults: {
      siteId: sites.some(s => s.id === saved?.siteId) ? saved?.siteId : undefined,
      companies: (saved?.companies || []).filter(p => sites.some(s => s.id === p.siteId && s.companies.some(c => c.id === p.companyId))),
    },
    suggested: { ...(site ? { siteId: site.id } : {}), ...(company ? { companyId: company.id } : {}) },
  };
}
export type WorkspaceChoices = ReturnType<typeof available>;
const entityInput = z.object({ id: scopedId.optional(), name, active: z.boolean().default(true) }).strict();
const grantInput = z.discriminatedUnion("scope", [
  z.object({ subjectId: scopedId, scope: z.literal("site"), siteId: scopedId, roleIds: canonicalRoles, active: z.boolean().default(true) }).strict(),
  z.object({ subjectId: scopedId, scope: z.literal("company"), companyId: scopedId, roleIds: canonicalRoles, active: z.boolean().default(true) }).strict(),
]);
export class WorkspaceDirectoryService {
  constructor(readonly store: WorkspaceDirectoryStore) {}
  private async state() { return workspaceDirectorySchema.parse(await this.store.read()); }
  async choices(subjectId: string): Promise<WorkspaceChoices> { scopedId.parse(subjectId); return available(await this.state(), subjectId); }
  async resolve(subjectId: string, selection: WorkspaceSelection) { return decision(await this.state(), subjectId, selection); }
  private canManage(state: WorkspaceDirectory, actor: WorkspaceActor, siteId: string, companyId?: string) {
    scopedId.parse(actor.subjectId);
    if (actor.root === true) return;
    const permitted = state.grants.some(g => g.subjectId === actor.subjectId && g.active && g.roleIds.includes("tenant.administrator") &&
      (g.scope === "site" ? g.siteId === siteId && state.sites.some(s => s.id === siteId && s.active) : g.companyId === companyId && state.companies.some(c => c.id === companyId && c.siteId === siteId && c.active) && state.sites.some(s => s.id === siteId && s.active)));
    if (!permitted) throw new AccessError("WORKSPACE_MANAGEMENT_DENIED", 403);
  }
  private async mutate<T>(expected: number, change: (state: WorkspaceDirectory) => T) {
    revision.parse(expected);
    const state = await this.state();
    if (state.revision !== expected) throw new AccessError("WORKSPACE_DIRECTORY_STALE", 409);
    state.revision++;
    const result = change(state);
    await this.store.compareAndSwap(state, expected);
    return structuredClone(result);
  }
  async saveSite(actor: WorkspaceActor, raw: z.input<typeof entityInput>, expected: number) {
    const input = entityInput.parse(raw);
    return this.mutate(expected, state => {
      if (actor.root !== true) throw new AccessError("WORKSPACE_MANAGEMENT_DENIED", 403);
      scopedId.parse(actor.subjectId);
      const existing = input.id && state.sites.find(s => s.id === input.id);
      if (input.id && !existing) throw new AccessError("WORKSPACE_NOT_FOUND", 404);
      const site = { ...input, id: existing ? existing.id : crypto.randomUUID(), revision: state.revision };
      if (existing) Object.assign(existing, site); else state.sites.push(site);
      return site;
    });
  }
  async saveCompany(actor: WorkspaceActor, siteId: string, raw: z.input<typeof entityInput>, expected: number) {
    scopedId.parse(siteId); const input = entityInput.parse(raw);
    return this.mutate(expected, state => {
      const existing = input.id && state.companies.find(c => c.id === input.id && c.siteId === siteId);
      this.canManage(state, actor, siteId, existing ? existing.id : undefined);
      if (!state.sites.some(s => s.id === siteId) || (input.id && !existing)) throw new AccessError("WORKSPACE_NOT_FOUND", 404);
      const company = { ...input, id: existing ? existing.id : crypto.randomUUID(), siteId, tenantId: existing ? existing.tenantId : `workspace_${crypto.randomUUID()}`, revision: state.revision };
      if (existing) Object.assign(existing, company); else state.companies.push(company);
      return company;
    });
  }
  async saveGrant(actor: WorkspaceActor, raw: z.input<typeof grantInput>, expected: number) {
    const input = grantInput.parse(raw);
    return this.mutate(expected, state => {
      const company = input.scope === "company" ? state.companies.find(c => c.id === input.companyId) : undefined;
      const siteId = input.scope === "site" ? input.siteId : company?.siteId;
      if (!siteId) throw new AccessError("WORKSPACE_NOT_FOUND", 404);
      this.canManage(state, actor, siteId, company?.id);
      if (!state.sites.some(s => s.id === siteId)) throw new AccessError("WORKSPACE_NOT_FOUND", 404);
      const existing = state.grants.find(g => g.subjectId === input.subjectId && g.scope === input.scope &&
        (g.scope === "site" && input.scope === "site" ? g.siteId === input.siteId : g.scope === "company" && input.scope === "company" && g.companyId === input.companyId));
      const grant = { ...input, id: existing?.id || crypto.randomUUID(), revision: state.revision };
      if (existing) Object.assign(existing, grant); else state.grants.push(grant);
      return grant;
    });
  }
  async remember(subjectId: string, selection: WorkspaceSelection, options: { site?: boolean; company?: boolean }, expected: number) {
    return this.mutate(expected, state => {
      decision(state, subjectId, selection);
      let preference = state.preferences.find(p => p.subjectId === subjectId);
      if (!preference) { preference = { subjectId, companies: [] }; state.preferences.push(preference); }
      if (options.site) preference.siteId = selection.siteId;
      if (options.company) preference.companies = [...preference.companies.filter(c => c.siteId !== selection.siteId), { ...selection }];
      return available(state, subjectId);
    });
  }
}
